import {
  BreadcrumbOverridersContext,
  breadcrumbSlot,
} from "@redotech/arbiter/breadcrumb";
import { useRequiredContext } from "@redotech/react-util/context";
import {
  canAccessCatalog,
  canAccessCatalogBillOfMaterials,
  useCanAccessCatalog,
} from "@redotech/redo-merchant-app-common/catalog/catalog-access";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { memo, useContext, useEffect } from "react";
import { matchPath, Outlet } from "react-router-dom";
import { unlinkCatalogListBreadcrumb } from "./catalog-list-paths";
import { createCatalogProductsRoute } from "./catalog-products-route";
import { RedoOutletAccessGuard } from "./inventory-routes";
import { canManageLandedCostPages } from "./landed-cost-pages-access";
import { defaultChildRoute } from "./legacy-redirects";

const catalogProductsViewBreadcrumb = breadcrumbSlot();
const productFamilyDetailBreadcrumb = breadcrumbSlot();
const productDetailBreadcrumb = breadcrumbSlot();
const billOfMaterialsDetailBreadcrumb = breadcrumbSlot();
const bomTemplateBindingBreadcrumb = breadcrumbSlot();

const CATALOG_ROUTE_BASE = "/stores/:storeId/catalog";
const HIDE_PADDING_ROUTES: string[] = [
  "/variants",
  "/products",
  "/products/:viewName",
  "/products/:viewName/:productFamilyId",
  "/products/:viewName/:productFamilyId/:productId",
  "/products/:viewName/:productFamilyId/:productId/locations/:locationId",
  "/consignment",
  "/consigned-product-families/:productFamilyId",
  "/bill-of-materials",
  "/bill-of-materials/new",
  "/bill-of-materials/:billOfMaterialsId",
];

const PADDED_ROUTES: string[] = ["/bill-of-materials/templates"];

function hidePadding(url: string): boolean {
  if (
    PADDED_ROUTES.some(
      (route) => !!matchPath(`${CATALOG_ROUTE_BASE}${route}`, url),
    )
  ) {
    return false;
  }
  return HIDE_PADDING_ROUTES.some(
    (route) => !!matchPath(`${CATALOG_ROUTE_BASE}${route}`, url),
  );
}

const CATALOG_LIST_BREADCRUMB_OVERRIDER = "catalog:unlinkListBreadcrumbs";

/**
 * Product pages stay reachable when the Catalog list is not (Catalog disabled
 * for the team), so their breadcrumbs must not link back to the list. Mounted
 * as a layout route under `Page`, which provides the merchant routes.
 */
const CatalogListBreadcrumbLinks = memo(function CatalogListBreadcrumbLinks() {
  const canAccessList = useCanAccessCatalog();
  const { catalogBaseUrl } = useRequiredContext(MerchantRoutesContext);
  const [, setBreadcrumbOverriders] = useContext(BreadcrumbOverridersContext);

  useEffect(() => {
    if (canAccessList) {
      return;
    }
    setBreadcrumbOverriders((prev) =>
      new Map(prev).set(CATALOG_LIST_BREADCRUMB_OVERRIDER, (breadcrumb) =>
        unlinkCatalogListBreadcrumb(breadcrumb, catalogBaseUrl),
      ),
    );
    return () => {
      setBreadcrumbOverriders((prev) => {
        if (!prev.has(CATALOG_LIST_BREADCRUMB_OVERRIDER)) {
          return prev;
        }
        const next = new Map(prev);
        next.delete(CATALOG_LIST_BREADCRUMB_OVERRIDER);
        return next;
      });
    };
  }, [canAccessList, catalogBaseUrl, setBreadcrumbOverriders]);

  return <Outlet />;
});

export const catalogRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Catalog" },
    async lazy() {
      const { Page } = await import("../../page");
      const { ImsStatusWrapper } =
        await import("@redotech/redo-merchant-inventory-app/components/ims-status-wrapper");
      return {
        element: (
          <ImsStatusWrapper>
            <Page hidePadding={hidePadding} />
          </ImsStatusWrapper>
        ),
      };
    },
    children: [
      {
        element: <CatalogListBreadcrumbLinks />,
        children: [
          defaultChildRoute("products"),
          createCatalogProductsRoute({
            catalogProductsViewBreadcrumb,
            productDetailBreadcrumb,
            productFamilyDetailBreadcrumb,
            showProductModal: true,
            listPermission: canAccessCatalog,
          }),
          {
            path: "variants",
            handle: { breadcrumb: "Variants", permission: canAccessCatalog },
            async lazy() {
              const { ProductBinsPage } =
                await import("@redotech/redo-merchant-inventory-app/product-bins/product-bins-page");
              return { Component: ProductBinsPage };
            },
          },
          {
            path: "bill-of-materials",
            handle: {
              breadcrumb: "Bill of materials",
              permission: canAccessCatalogBillOfMaterials,
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { BillOfMaterialsListPage } =
                    await import("@redotech/redo-merchant-inventory-app/bill-of-materials/bill-of-materials-list");
                  return { Component: BillOfMaterialsListPage };
                },
              },
              {
                path: "templates",
                handle: { breadcrumb: "Templates" },
                children: [
                  {
                    index: true,
                    async lazy() {
                      const { BomTemplateBindingsPage } =
                        await import("@redotech/redo-merchant-inventory-app/bill-of-materials/templates/bom-template-bindings-page");
                      return { Component: BomTemplateBindingsPage };
                    },
                  },
                  {
                    path: ":bindingId",
                    handle: { breadcrumb: bomTemplateBindingBreadcrumb },
                    async lazy() {
                      const { BomTemplateBindingPage } =
                        await import("@redotech/redo-merchant-inventory-app/bill-of-materials/templates/bom-template-binding-page");
                      return {
                        element: (
                          <BomTemplateBindingPage
                            breadcrumb={bomTemplateBindingBreadcrumb}
                          />
                        ),
                      };
                    },
                  },
                ],
              },
              {
                path: "new",
                handle: { breadcrumb: "New" },
                async lazy() {
                  const { BillOfMaterialsDetailPage } =
                    await import("@redotech/redo-merchant-inventory-app/bill-of-materials/bill-of-materials-detail");
                  return {
                    element: (
                      <BillOfMaterialsDetailPage
                        breadcrumb={billOfMaterialsDetailBreadcrumb}
                      />
                    ),
                  };
                },
              },
              {
                path: ":billOfMaterialsId",
                handle: { breadcrumb: billOfMaterialsDetailBreadcrumb },
                async lazy() {
                  const { BillOfMaterialsDetailPage } =
                    await import("@redotech/redo-merchant-inventory-app/bill-of-materials/bill-of-materials-detail");
                  return {
                    element: (
                      <BillOfMaterialsDetailPage
                        breadcrumb={billOfMaterialsDetailBreadcrumb}
                      />
                    ),
                  };
                },
              },
            ],
          },
          {
            path: "consigned-product-families/:productFamilyId",
            handle: { breadcrumb: productFamilyDetailBreadcrumb },
            async lazy() {
              const { ConsignedProductFamilyView } =
                await import("@redotech/redo-merchant-catalog-app/catalog-consignment/consigned-product-family-view");
              return {
                element: (
                  <RedoOutletAccessGuard>
                    <ConsignedProductFamilyView />
                  </RedoOutletAccessGuard>
                ),
              };
            },
          },
          {
            path: "restrictions",
            handle: {
              breadcrumb: "Restrictions",
              permission: canManageLandedCostPages,
            },
            async lazy() {
              const { RestrictionsSettingsPage } =
                await import("@redotech/redo-merchant-fulfillment-app/setting/restrictions/restrictions-page");
              return { Component: RestrictionsSettingsPage };
            },
          },
          {
            path: "consignment",
            handle: { breadcrumb: "Consignment", permission: canAccessCatalog },
            async lazy() {
              const { CatalogConsignmentProducts } =
                await import("@redotech/redo-merchant-catalog-app/catalog-consignment/catalog-consignment-products");
              return {
                Component: () => (
                  <RedoOutletAccessGuard>
                    <CatalogConsignmentProducts />
                  </RedoOutletAccessGuard>
                ),
              };
            },
          },
        ],
      },
    ],
  },
];
